import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Users, Plus, Tag, Pencil, Link2, ChevronDown, ChevronRight } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { NurseryCustomer, NurseryCustomerTier } from '../../types/nursery';

function CustomerList() {
  const navigate = useNavigate();
  const { hasAccess: canManage } = useModuleLicense('accounts', ['user', 'admin']);

  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Head offices whose sites are showing. Collapsed to start with; a search
  // opens them all, since the match may be a site.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Inline tier management
  const [showTiers, setShowTiers] = useState(false);
  const [newTierName, setNewTierName] = useState('');
  const [newTierDiscount, setNewTierDiscount] = useState('');
  const [newTierRequiresApproval, setNewTierRequiresApproval] = useState(false);
  const [newTierPaymentMode, setNewTierPaymentMode] = useState<'online' | 'on_account'>('on_account');
  const [savingTier, setSavingTier] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [custRes, tierRes] = await Promise.all([
        nurseryService.getCustomers({ active: true, ...(search ? { search } : {}) }),
        nurseryService.getCustomerTiers(),
      ]);
      if (custRes.success) setCustomers(custRes.data || []);
      if (tierRes.success) setTiers(tierRes.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // Sites sit under their head office. A site whose head office is not in the
  // list (a search matched the site alone) stands on its own row and names it.
  const { topLevel, sitesOf } = useMemo(() => {
    const ids = new Set(customers.map(c => c.id));
    const byParent = new Map<number, NurseryCustomer[]>();
    const top: NurseryCustomer[] = [];
    for (const c of customers) {
      if (c.parent_customer_id && ids.has(c.parent_customer_id)) {
        const list = byParent.get(c.parent_customer_id) ?? [];
        list.push(c);
        byParent.set(c.parent_customer_id, list);
      } else {
        top.push(c);
      }
    }
    return { topLevel: top, sitesOf: byParent };
  }, [customers]);

  const searching = search.trim() !== '';
  const toggle = (id: number) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderRow = (c: NurseryCustomer, indented: boolean) => {
    const sites = sitesOf.get(c.id) ?? [];
    const siteCount = Number(c.site_count ?? sites.length) || 0;
    const open = searching || expanded.has(c.id);
    const orphanSite = !indented && Boolean(c.parent_customer_id);
    return (
      <div key={c.id}>
        <div className={`flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 ${indented ? 'pl-8 sm:pl-12 bg-neutral-50/50' : ''}`}>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Link to={`/accounts/customers/${c.id}`} className="font-medium text-neutral-800 hover:text-primary-700 hover:underline break-words min-w-0">
                {c.name}
              </Link>
              {c.customer_code && <span className="text-xs text-neutral-500 break-words min-w-0">#{c.customer_code}</span>}
              {c.tier_name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 break-words min-w-0">
                  {c.tier_name}
                </span>
              )}
              {c.linked_org_id && (
                <span className="inline-flex items-center gap-1 text-xs text-neutral-400" title={c.linked_org_name || 'Linked organisation'}>
                  <Link2 className="h-3 w-3" /> linked
                </span>
              )}
            </div>
            {orphanSite && (
              <p className="text-xs text-neutral-500 break-words">
                Site of{' '}
                <Link to={`/accounts/customers/${c.parent_customer_id}`} className="text-primary-600 hover:underline">
                  {c.parent_name || 'its head office'}
                </Link>
              </p>
            )}
            <p className="text-xs text-neutral-500 break-words">
              {[c.contact_name, c.email, c.phone].filter(Boolean).join(' · ') || 'No contact details'}
            </p>
            {siteCount > 0 && (sites.length > 0 ? (
              <button
                type="button"
                onClick={() => toggle(c.id)}
                aria-expanded={open}
                disabled={searching}
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline disabled:no-underline disabled:text-neutral-600"
              >
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {siteCount} site{siteCount === 1 ? '' : 's'}
                {searching && sites.length < siteCount ? ` (${sites.length} shown)` : ''}
              </button>
            ) : (
              <p className="mt-1 text-xs text-neutral-500">{siteCount} site{siteCount === 1 ? '' : 's'}</p>
            ))}
          </div>
          {canManage && (
            <button
              onClick={() => navigate(`/accounts/customers/${c.id}/edit`)}
              className="text-neutral-400 hover:text-primary-600 p-1 shrink-0"
              title="Edit"
              aria-label={`Edit ${c.name}`}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
        {open && sites.length > 0 && (
          <div className="divide-y divide-neutral-100 border-t border-neutral-100">
            {sites.map(site => renderRow(site, true))}
          </div>
        )}
      </div>
    );
  };

  const refreshTiers = async () => {
    const tierRes = await nurseryService.getCustomerTiers();
    if (tierRes.success) setTiers(tierRes.data || []);
  };

  const addTier = async () => {
    if (!newTierName.trim()) return;
    setSavingTier(true);
    try {
      const res = await nurseryService.createCustomerTier({
        name: newTierName.trim(),
        default_discount_pct: newTierDiscount ? Number(newTierDiscount) : 0,
        requires_approval: newTierRequiresApproval,
        payment_mode: newTierPaymentMode,
      });
      if (res.success) {
        setNewTierName('');
        setNewTierDiscount('');
        setNewTierRequiresApproval(false);
        setNewTierPaymentMode('on_account');
        await refreshTiers();
      }
    } finally {
      setSavingTier(false);
    }
  };

  const patchTier = async (id: number, data: Partial<NurseryCustomerTier>) => {
    const res = await nurseryService.updateCustomerTier(id, data);
    if (res.success) await refreshTiers();
  };

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Customer registry, pricing tiers and contacts"
        icon={<Users className="h-6 w-6 text-white" />}
        actionButtons={
          canManage && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowTiers(s => !s)}>
                <Tag className="h-4 w-4" /> Tiers
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/accounts/customers/new')}>
                <Plus className="h-4 w-4" /> New Customer
              </Button>
            </div>
          )
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {showTiers && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-1">Pricing Tiers</h3>
            <p className="text-xs text-neutral-400 mb-4">
              Tiers scope price lists and default discounts. The <strong>website default</strong> tier is what
              self-signup (retail) customers get; tiers marked <strong>requires approval</strong> are wholesale
              tiers customers apply for. <strong>Payment mode</strong> decides whether the customer pays online
              at checkout or is invoiced on account.
            </p>

            {tiers.length === 0 ? (
              <p className="text-sm text-neutral-400 mb-4">No tiers yet.</p>
            ) : (
              <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 mb-4">
                {tiers.map(t => (
                  <div key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 text-sm">
                    <span className="font-medium text-neutral-800 min-w-[8rem]">
                      {t.name}{Number(t.default_discount_pct) > 0 ? ` · ${t.default_discount_pct}%` : ''}
                    </span>
                    {canManage ? (
                      <>
                        <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
                          <input
                            type="radio"
                            name="website-default-tier"
                            checked={t.is_website_default}
                            onChange={() => patchTier(t.id, { is_website_default: true })}
                          />
                          Website default
                        </label>
                        <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
                          <input
                            type="checkbox"
                            checked={t.requires_approval}
                            onChange={e => patchTier(t.id, { requires_approval: e.target.checked })}
                          />
                          Requires approval
                        </label>
                        <select
                          value={t.payment_mode}
                          onChange={e => patchTier(t.id, { payment_mode: e.target.value as 'online' | 'on_account' })}
                          className="text-xs border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="online">Pays online</option>
                          <option value="on_account">On account</option>
                        </select>
                      </>
                    ) : (
                      <span className="text-xs text-neutral-500">
                        {t.is_website_default ? 'Website default · ' : ''}
                        {t.requires_approval ? 'Requires approval · ' : ''}
                        {t.payment_mode === 'online' ? 'Pays online' : 'On account'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {canManage && (
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[10rem]">
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Tier name</label>
                  <input
                    value={newTierName}
                    onChange={e => setNewTierName(e.target.value)}
                    placeholder="e.g. Garden Centre"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="w-28">
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Default disc. %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newTierDiscount}
                    onChange={e => setNewTierDiscount(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="w-36">
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Payment</label>
                  <select
                    value={newTierPaymentMode}
                    onChange={e => setNewTierPaymentMode(e.target.value as 'online' | 'on_account')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="online">Pays online</option>
                    <option value="on_account">On account</option>
                  </select>
                </div>
                <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600 pb-2.5">
                  <input
                    type="checkbox"
                    checked={newTierRequiresApproval}
                    onChange={e => setNewTierRequiresApproval(e.target.checked)}
                  />
                  Requires approval
                </label>
                <Button variant="outline" size="sm" onClick={addTier} disabled={savingTier || !newTierName.trim()}>
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search customers"
            placeholder="Search by name or store number"
            className="w-full max-w-sm border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No customers found</p>
            {canManage && (
              <Button variant="primary" className="mt-4" onClick={() => navigate('/accounts/customers/new')}>
                <Plus className="h-4 w-4" /> New Customer
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100 overflow-hidden">
            {topLevel.map(c => renderRow(c, false))}
          </div>
        )}
      </div>
    </>
  );
}

export default CustomerList;
